import useSWR, { SWRResponse } from "swr";

import { useAuth, useSWRApiErrorHandler } from "../common/hooks";
import { PaymentFormValues } from "../features/payment/components/PaymentForm";
import { DirectorRejectPaymentFormValues } from "../features/payment/hooks/useDirectorRejectPaymentHandler";
import {
    AmountString,
    BankAccount,
    StateAndRef,
    UploadedFile,
    UUID,
    WorkflowResult,
} from "../types/common";
import { InvoiceState } from "../types/invoice";
import {
    AccountInfo,
    bankAccountToAccountInfo,
    Participant,
    PaymentReceiptState,
    PaymentState,
    PaymentStateWithDocuments,
    PreAndPaymentStateWithDocuments,
    PreSubmitPaymentState,
    PreSubmitHistory,
} from "../types/payment";
import { stringifyAmount } from "../utils/amount";
import { isLtaProject } from "../utils/project";

import request, { ApiError, requestWithToken } from "./common";

const paymentApi = {
    create: (
        invoiceRef: string,
        paymentFormValues: PaymentFormValues,
        invoice: InvoiceState,
        payerList: BankAccount[],
        projectName: string,
    ): Promise<PaymentOrderStatetWorkflowResult> => {

        const selectedPayerAccount = payerList.find(
//             (it) => it.bankAccountHolderName === paymentFormValues.bankAccount,
            (it) => {
                return (it.bankAccountHolderName + '-' + it.bankAccountNumber) === paymentFormValues.bankAccount
            },
        );
        const legalName = !!selectedPayerAccount ? selectedPayerAccount.bankName : "";
        const payload: PaymentCreationRequest = {
            amount: stringifyAmount(paymentFormValues),
            housingRules: paymentFormValues.housingRulesTypes,
            chargeBearer: "SHAR",
            fromAccount: bankAccountToAccountInfo(selectedPayerAccount),
            invoiceDocumentsToShare: invoice.props.documents,
            payer: {
                legalName: legalName,
                lei: "",
                bic: selectedPayerAccount?.swiftAddress,
                addressLn1: selectedPayerAccount?.registeredAddress,
            },
            paymentType: isLtaProject(projectName) ? projectName : "-",
            purposeCode: paymentFormValues.purposeCode,
            remittanceInformation:
                paymentFormValues.remittanceInformation.split("\n"),
        };
        return request<
            PaymentOrderStatetWorkflowResult,
            PaymentCreationRequest
        >(`/payment-order/${invoiceRef}`, "POST", payload);
    },
    directorApprove: (
        ref: string,
    ): Promise<PaymentOrderStatetWorkflowResult> => {
        return request<PaymentOrderStatetWorkflowResult>(
            `/payment-order/request/${ref}`,
            "POST",
        );
    },
    directorReject: (
        ref: string,
        values: DirectorRejectPaymentFormValues,
    ): Promise<PaymentOrderStatetWorkflowResult> => {
        const payload: PaymentRejectRequest = {
            comment: values.comment,
        };
        return request<PaymentOrderStatetWorkflowResult, PaymentRejectRequest>(
            `/payment-order/reject/${ref}`,
            "POST",
            payload,
        );
    },
    getPreSubmitPayment: (
        linearId: UUID,
    ): Promise<StateAndRef<PreSubmitPaymentState>> => {
        return request<StateAndRef<PreSubmitPaymentState>>(
            `/payment-order/linearId/${linearId}/latest`,
        );
    },
    getPreSubmitHistory: (
        linearId: UUID,
    ): Promise<PreSubmitHistory> => {
        return request<PreSubmitHistory>(
            `/payment-order/history/${linearId}`,
        );
    },
    getHistory: (linearId: UUID): Promise<StateAndRef<PaymentState>[]> => {
        return request<StateAndRef<PaymentState>[]>(
            `/payment/history/${linearId}`,
        );
    },
    getByLinearId: (linearId: UUID): Promise<StateAndRef<PaymentState>> => {
        return request<StateAndRef<PaymentState>>(
            `/payment/linearId/${linearId}/latest`,
        );
    },
};

export const usePreAndPaymentList = (
    invoiceLinearId: UUID,
): SWRResponse<PreAndPaymentStateWithDocuments[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<PreAndPaymentStateWithDocuments[], ApiError>(
        `/payment/pre/list/${invoiceLinearId}`,
        request,
        { onError },
    );
};

export const usePaymentWithToken = (
    linearId: UUID,
): SWRResponse<PaymentStateWithDocuments, ApiError> => {
    const { token } = useAuth();
    return useSWR<PaymentStateWithDocuments, ApiError>(
        token && linearId
            ? `/token-access/payment/linearId/${linearId}/latest/withDocuments`
            : null,
        requestWithToken(token),
    );
};

export const usePaymentReceipt = (
    linearId: UUID,
): SWRResponse<PaymentReceiptState, ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<PaymentReceiptState, ApiError>(
        `/payment/receipt/detail/${linearId}`,
        request,
        { onError },
    );
};

export const usePaymentReceiptListByProject = (
    projectId: UUID,
): SWRResponse<PaymentReceiptState[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<PaymentReceiptState[], ApiError>(
        `/payment/receipt/p/${projectId}`,
        request,
        { onError },
    );
};

export const useUnmatchedPaymentReceiptList = (): SWRResponse<
    PaymentReceiptState[],
    ApiError
> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<PaymentReceiptState[], ApiError>(
        "/payment/receipt/unmatched/all",
        request,
        { onError },
    );
};

type PaymentCreationRequest = {
    amount: AmountString;
    housingRules: string[];
    chargeBearer: string;
    fromAccount: AccountInfo;
    invoiceDocumentsToShare: UploadedFile[];
    payer: Participant;
    paymentType: string;
    purposeCode: string;
    remittanceInformation: string[];
};

type PaymentRejectRequest = {
    comment: string;
};

type PaymentOrderStatetWorkflowResult = WorkflowResult<{
    PaymentOrderState: StateAndRef<PreSubmitPaymentState>[];
    InvoiceState: StateAndRef<InvoiceState>[];
}>;

export default paymentApi;
